import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import Spinner from "../../Components/Spinner";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { ToastComponent } from "../../Components/ToastComponent";
import {
  VEHICLE_GROUP_REPORT_ITEMS,
  VEHICLE_GROUP_LEGACY_REPORT_TYPES,
  HIGH_SPEED_OPTIONS,
  MOVEMENT_REPORT_CODE_PDF,
  MOVEMENT_REPORT_CODE_EXCEL,
} from "./constants";
import { useEmailConfiguration } from "./useEmailConfiguration";
import { useGroupReportConfig } from "./useGroupReportConfig";
import { useVehicleConfigSubmit } from "./useVehicleConfigSubmit";
import {
  loadVehicleConfigState,
  useVehicleConfigStorage,
} from "./useVehicleConfigStorage";
import ColorConfigCard from "./sections/ColorConfigCard";
import SpeedConfigCard from "./sections/SpeedConfigCard";
import MovementConfigCard from "./sections/MovementConfigCard";
import OtherReportsCard from "./sections/OtherReportsCard";
import CopyGroupConfig from "./sections/CopyGroupConfig";


export default function VehiclesConfig() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { item } = location.state || {};

  const initialStoredState = loadVehicleConfigState(item?.vehicleGroupId);

  const {
    isLoading,
    group,
    listCollor,
    selectedId, setSelectedId,
    selectedColor, setSelectedColor,
    kmzFlag, setKmzFlag,
    fenceFlag, setFenceFlag,
    visitFlag, setVisitFlag,
    riskAreaFlag, setRiskAreaFlag,
    strechesFlag, setStrechesFlag,
    relatorios, setRelatorios,
    archives, setArchives,
    movementForm, setMovementForm,
    speedForm, setSpeedForm,
    reportConfigPayload, setReportConfigPayload,
    initialReportConfig, setInitialReportConfig,
  } = useGroupReportConfig({
    vehicleGroupId: item?.vehicleGroupId,
    initialStoredState,
  });

  const reportItems = VEHICLE_GROUP_REPORT_ITEMS;

  useVehicleConfigStorage(item?.vehicleGroupId, {
    selectedId,
    selectedColor,
    kmzFlag,
    fenceFlag,
    visitFlag,
    riskAreaFlag,
    strechesFlag,
    relatorios,
    archives,
    movementForm,
    speedForm,
    reportConfigPayload,
  });

  const REPORT_TYPES = useMemo(
    () => [
      ...reportItems.map((reportItem) => ({
        key: reportItem.id,
        codeReport: reportItem.codeReport,
        nome: reportItem.nome,
      })),
      {
        key: "movimentacaoPdf",
        codeReport: MOVEMENT_REPORT_CODE_PDF,
        nome: "Relatório de Movimentação PDF",
      },
      {
        key: "movimentacaoExcel",
        codeReport: MOVEMENT_REPORT_CODE_EXCEL,
        nome: "Relatório de Movimentação Excel",
      },
      ...VEHICLE_GROUP_LEGACY_REPORT_TYPES,
    ],
    [reportItems]
  );

  const {
    emailsByReport,
    setEmailsByReport,
    loadingButtons,
    setLoadingButtons,
    fetchEmails,
  } = useEmailConfiguration({
    vehicleGroupId: item?.vehicleGroupId,
    reportTypes: REPORT_TYPES,
  });

  const { isSaving, toastParams, setToastParams, onSubmit } = useVehicleConfigSubmit({
    group,
    selectedId,
    movementForm,
    speedForm,
    visitFlag,
    fenceFlag,
    riskAreaFlag,
    strechesFlag,
    archives,
    reportItems,
    reportConfigPayload,
    REPORT_TYPES,
  });

  useEffect(() => {
    const contentContainer = document.querySelector(".movyx-content");
    if (!contentContainer) return undefined;
    contentContainer.classList.add("allow-localization-scroll");
    return () => {
      contentContainer.classList.remove("allow-localization-scroll");
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "title", value: "Configuração do grupo" });
  }, [dispatch]);

  const handleChangeCollor = (e) => {
    const colorId = e.target.value;
    const colorObj = listCollor.find((c) => c.colorId == colorId);
    setSelectedColor(colorObj?.hex || "");
    setSelectedId(colorId);
  };

  const handleCancel = () => {
    navigate(-1);
  };


  const handleCopyGroup = (config) => {
    setSelectedId(config.colorId);
    setSelectedColor(config.selectedColor);
    setKmzFlag(config.kmzFlag);
    setFenceFlag(config.fenceFlag);
    setVisitFlag(config.visitFlag);
    setRiskAreaFlag(config.riskAreaFlag);
    setStrechesFlag(config.strechesFlag);
    setRelatorios(config.relatorios);
    setArchives(config.archives);
    setMovementForm(config.movementForm);
    setSpeedForm(config.speedForm);
    setReportConfigPayload(config.reportConfigPayload);
    setInitialReportConfig(config.reportConfigPayload);
  };

  return (
    <>
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <div
        style={{
          marginLeft: 10,
          marginRight: 10,
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 20,
        }}
      >
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="back-to-fleet" style={{ marginBottom: 8 }}>
              <button
                style={{ display: "flex", alignItems: "center" }}
                onClick={() => handleCancel()}
              >
                <div className="icon-back-fleet">
                  <IoMdArrowBack />
                </div>
                <span>Voltar</span>
              </button>
            </div>
            <div className="">
              <h3 style={{ color: "var(--text-primary)" }}>
                Configurando grupo: {item.vehicleGroupName}
              </h3>
            </div>

            {/* ── Copy-from-group control ────────────────────────────────── */}
            <CopyGroupConfig
              currentGroupId={item?.vehicleGroupId}
              listCollor={listCollor}
              onCopy={handleCopyGroup}
            />

            <div className="row">
              <ColorConfigCard
                listCollor={listCollor}
                selectedId={selectedId}
                selectedColor={selectedColor}
                onColorChange={handleChangeCollor}
              />
              <SpeedConfigCard
                speedForm={speedForm}
                onSpeedFormChange={setSpeedForm}
                highSpeedOptions={HIGH_SPEED_OPTIONS}
              />
              <MovementConfigCard
                movementForm={movementForm}
                onMovementFormChange={setMovementForm}
                vehicleGroupId={item?.vehicleGroupId}
                reportTypes={REPORT_TYPES}
                emailsByReport={emailsByReport}
                setEmailsByReport={setEmailsByReport}
                loadingButtons={loadingButtons}
                setLoadingButtons={setLoadingButtons}
                fetchEmails={fetchEmails}
              />
              <OtherReportsCard
                reportItems={VEHICLE_GROUP_REPORT_ITEMS}
                initialReportConfig={initialReportConfig}
                reportConfigPayload={reportConfigPayload}
                setReportConfigPayload={setReportConfigPayload}
                relatorios={relatorios}
                setRelatorios={setRelatorios}
                visitFlag={visitFlag}
                setVisitFlag={setVisitFlag}
                fenceFlag={fenceFlag}
                setFenceFlag={setFenceFlag}
                riskAreaFlag={riskAreaFlag}
                setRiskAreaFlag={setRiskAreaFlag}
                vehicleGroupId={item.vehicleGroupId}
                reportTypes={REPORT_TYPES}
                emailsByReport={emailsByReport}
                setEmailsByReport={setEmailsByReport}
                loadingButtons={loadingButtons}
                setLoadingButtons={setLoadingButtons}
                fetchEmails={fetchEmails}
              />
            </div>
            <div
              style={{
                marginTop: 20,
                textAlign: "right",
                display: "flex",
                gap: 16,
                justifyContent: "end",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => handleCancel()}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => onSubmit()}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Salvando...
                  </>
                ) : (
                  t("BUTTONS.SAVE")
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
